import React, { useCallback, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import { StyleSheet, View } from 'react-native';

import {
  CONTENT_CONTAINER_PADDING_MOBILE,
  ListCardGroupContainer,
  NUM_COLUMNS,
  RenderItemRowContainer,
} from 'components/list-card-group/styled';
import { ListCardGroupVariant } from 'components/list-card-group/types';
import { formatData, groupItemsInRows } from 'components/list-card-group/utils';

interface ComboSlotOptionsGridRenderItemParams<T> {
  item: T;
  index: number;
  rowIndex: number;
}

interface ComboSlotOptionsGridProps<T> {
  items: T[];
  numColumns?: number;
  selectedRow?: number;
  inlineItemCustomization?: React.ReactNode | null;
  isFullContainer?: boolean;
  renderItem: (params: ComboSlotOptionsGridRenderItemParams<T>) => React.ReactNode;
}

export function splitRowsAtIndex<T>(rows: T[][], selectedRow: number | undefined) {
  if (selectedRow === undefined || selectedRow < 0) {
    return { rowsBefore: rows, rowsAfter: [] as T[][] };
  }

  return {
    rowsBefore: rows.slice(0, selectedRow + 1),
    rowsAfter: rows.slice(selectedRow + 1),
  };
}

export function ComboSlotOptionsGrid<T>({
  items,
  numColumns = NUM_COLUMNS,
  selectedRow,
  inlineItemCustomization,
  isFullContainer = false,
  renderItem,
}: ComboSlotOptionsGridProps<T>) {
  const rows = useMemo(() => groupItemsInRows(formatData([...items], numColumns), numColumns), [
    items,
    numColumns,
  ]);

  const { rowsBefore, rowsAfter } = useMemo(
    () => splitRowsAtIndex(rows, inlineItemCustomization ? selectedRow : undefined),
    [inlineItemCustomization, rows, selectedRow]
  );

  const renderRow = useCallback(
    (rowItems: (T & { empty?: boolean })[], rowIndex: number) => (
      <RenderItemRowContainer
        key={`combo-slot-row-${rowIndex}`}
        listCardGroupVariant={ListCardGroupVariant.GROUP}
      >
        {rowItems.map((item, index) => {
          if (item.empty) {
            return <Box opacity={0} key={`empty-${index}`} />;
          }

          return renderItem({ item, index, rowIndex });
        })}
      </RenderItemRowContainer>
    ),
    [renderItem]
  );

  if (!rows.length) {
    return null;
  }

  return (
    <ListCardGroupContainer $isFullContainer={isFullContainer}>
      <View style={styles.container}>
        {rowsBefore.map((rowItems, rowIndex) => renderRow(rowItems, rowIndex))}
        {inlineItemCustomization ? <View>{inlineItemCustomization}</View> : null}
        {rowsAfter.map((rowItems, rowIndex) => renderRow(rowItems, rowsBefore.length + rowIndex))}
      </View>
    </ListCardGroupContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: CONTENT_CONTAINER_PADDING_MOBILE,
    width: '100%',
  },
});
